import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import {
  Clock, ArrowLeft, RefreshCw, Save, Play, Settings,
  CheckCircle, XCircle, Loader2, AlertTriangle, Bell, Shield,
  Calendar, Activity, FileText, Timer
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SchedulerPage() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState({});
  const [activeTab, setActiveTab] = useState('config');
  
  const [config, setConfig] = useState({
    enabled: true,
    alert_check: { enabled: true, interval_hours: 1 },
    daily_digest: { enabled: true, hour: 8, minute: 0 },
    deadline_reminder: { enabled: true, interval_hours: 24, days_ahead: 7 },
    compliance_scan: { enabled: false, interval_hours: 24 }
  });
  
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { headers } = getAuthHeaders();

      const [configRes, statusRes, logsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/scheduler/config`, { headers }),
        axios.get(`${API_URL}/api/scheduler/status`, { headers }),
        axios.get(`${API_URL}/api/scheduler/logs?limit=50`, { headers }),
        axios.get(`${API_URL}/api/scheduler/stats`, { headers })
      ]);

      setConfig(configRes.data);
      setStatus(statusRes.data);
      setLogs(logsRes.data?.logs || []);
      setStats(statsRes.data);

    } catch (error) {
      console.error('Error fetching scheduler data:', error);
      toast.error('Failed to load scheduler settings');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      const { headers } = getAuthHeaders();
      await axios.put(`${API_URL}/api/scheduler/config`, config, { headers });
      toast.success('Scheduler configuration saved');
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const toggleScheduler = async (enabled) => {
    try {
      const { headers } = getAuthHeaders();
      await axios.post(`${API_URL}/api/scheduler/toggle?enabled=${enabled}`, {}, { headers });
      setConfig(prev => ({ ...prev, enabled }));
      toast.success(`Scheduler ${enabled ? 'enabled' : 'disabled'}`);
      await fetchData();
    } catch (error) {
      toast.error('Failed to toggle scheduler');
    }
  };

  const runTask = async (taskName) => {
    setRunning(prev => ({ ...prev, [taskName]: true }));
    try {
      const { headers } = getAuthHeaders();
      await axios.post(`${API_URL}/api/scheduler/run/${taskName}`, {}, { headers });
      toast.success(`Task ${taskName.replace(/_/g, ' ')} executed`);
      await fetchData();
    } catch (error) {
      toast.error(`Failed to run ${taskName}`);
    } finally {
      setRunning(prev => ({ ...prev, [taskName]: false }));
    }
  };

  const updateTaskConfig = (taskName, field, value) => {
    setConfig(prev => ({
      ...prev,
      [taskName]: { ...prev[taskName], [field]: value }
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 animate-spin text-orange-500 mx-auto mb-4" />
          <p className="text-slate-600">Loading scheduler settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Header */}
      <header className="relative z-10 border-b border-slate-700/50 bg-gradient-to-r from-slate-900 to-slate-800 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/admin')}
                className="text-white/70 hover:text-white hover:bg-slate-50/10"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Admin
              </Button>
              <div className="h-6 w-px bg-slate-50/20"></div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center">
                  <Timer className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">Automated Scheduler</h1>
                  <p className="text-xs text-white/60">Configure automated tasks</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 mr-4">
                <span className="text-white/60 text-sm">Scheduler:</span>
                <Badge className={status?.scheduler_running ? 'bg-green-500' : 'bg-slate-500'}>
                  {status?.scheduler_running ? 'Running' : 'Stopped'}
                </Badge>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchData}
                className="border-white/20 text-white hover:bg-slate-50/10"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-6 py-6">
        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card className="bg-slate-50/90 border-slate-300">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Last 24 Hours</p>
                  <p className="text-2xl font-bold text-slate-900">{stats?.last_24h || 0}</p>
                </div>
                <Activity className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600">Success</p>
                  <p className="text-2xl font-bold text-green-700">
                    {Object.values(stats?.by_task || {}).reduce((a, b) => a + (b.success || 0), 0)}
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-red-50 border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600">Failed</p>
                  <p className="text-2xl font-bold text-red-700">
                    {Object.values(stats?.by_task || {}).reduce((a, b) => a + (b.failed || 0), 0)}
                  </p>
                </div>
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600">Active Tasks</p>
                  <p className="text-2xl font-bold text-blue-700">
                    {[config.alert_check, config.daily_digest, config.deadline_reminder, config.compliance_scan].filter(t => t.enabled).length}
                  </p>
                </div>
                <Clock className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100/80 border border-slate-300 mb-6">
            <TabsTrigger value="config" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white">
              <Settings className="w-4 h-4 mr-2" />
              Configuration
            </TabsTrigger>
            <TabsTrigger value="logs" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white">
              <FileText className="w-4 h-4 mr-2" />
              Execution Logs ({logs.length})
            </TabsTrigger>
          </TabsList>

          {/* Configuration Tab */}
          <TabsContent value="config" className="space-y-6">
            {/* Master Switch */}
            <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl ${config.enabled ? 'bg-green-500' : 'bg-slate-400'} flex items-center justify-center`}>
                      <Timer className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">Master Scheduler</h3>
                      <p className="text-sm text-slate-500">Enable or disable all automated tasks</p>
                    </div>
                  </div>
                  <Switch
                    checked={config.enabled}
                    onCheckedChange={toggleScheduler}
                  />
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-2 gap-6">
              {/* Alert Check */}
              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-slate-900 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                      TMF Alert Check
                    </CardTitle>
                    <Switch
                      checked={config.alert_check?.enabled || false}
                      onCheckedChange={(v) => updateTaskConfig('alert_check', 'enabled', v)}
                    />
                  </div>
                  <CardDescription>Scan studies for critical alerts and notify users</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Check Interval</Label>
                    <Select
                      value={String(config.alert_check?.interval_hours || 1)}
                      onValueChange={(v) => updateTaskConfig('alert_check', 'interval_hours', parseInt(v))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Every 1 hour</SelectItem>
                        <SelectItem value="2">Every 2 hours</SelectItem>
                        <SelectItem value="4">Every 4 hours</SelectItem>
                        <SelectItem value="6">Every 6 hours</SelectItem>
                        <SelectItem value="12">Every 12 hours</SelectItem>
                        <SelectItem value="24">Every 24 hours</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {status?.tasks?.alert_check?.last_run && (
                    <p className="text-xs text-slate-500">
                      Last run: {format(parseISO(status.tasks.alert_check.last_run), 'MMM dd, HH:mm')}
                    </p>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => runTask('alert_check')}
                    disabled={running.alert_check}
                    className="w-full"
                  >
                    {running.alert_check ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                    Run Now
                  </Button>
                </CardContent>
              </Card>

              {/* Daily Digest */}
              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-slate-900 flex items-center gap-2">
                      <Bell className="w-5 h-5 text-blue-600" />
                      Daily Digest
                    </CardTitle>
                    <Switch
                      checked={config.daily_digest?.enabled || false}
                      onCheckedChange={(v) => updateTaskConfig('daily_digest', 'enabled', v)}
                    />
                  </div>
                  <CardDescription>Send daily TMF health summary to admins</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>Hour (UTC)</Label>
                      <Select
                        value={String(config.daily_digest?.hour || 8)}
                        onValueChange={(v) => updateTaskConfig('daily_digest', 'hour', parseInt(v))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 24 }, (_, i) => (
                            <SelectItem key={i} value={String(i)}>{i.toString().padStart(2, '0')}:00</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Minute</Label>
                      <Select
                        value={String(config.daily_digest?.minute || 0)}
                        onValueChange={(v) => updateTaskConfig('daily_digest', 'minute', parseInt(v))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">:00</SelectItem>
                          <SelectItem value="15">:15</SelectItem>
                          <SelectItem value="30">:30</SelectItem>
                          <SelectItem value="45">:45</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {status?.tasks?.daily_digest?.last_run && (
                    <p className="text-xs text-slate-500">
                      Last run: {format(parseISO(status.tasks.daily_digest.last_run), 'MMM dd, HH:mm')}
                    </p>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => runTask('daily_digest')}
                    disabled={running.daily_digest}
                    className="w-full"
                  >
                    {running.daily_digest ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                    Run Now
                  </Button>
                </CardContent>
              </Card>

              {/* Deadline Reminder */}
              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-slate-900 flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-purple-600" />
                      Deadline Reminder
                    </CardTitle>
                    <Switch
                      checked={config.deadline_reminder?.enabled || false}
                      onCheckedChange={(v) => updateTaskConfig('deadline_reminder', 'enabled', v)}
                    />
                  </div>
                  <CardDescription>Send reminders for upcoming document deadlines</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>Check Interval</Label>
                      <Select
                        value={String(config.deadline_reminder?.interval_hours || 24)}
                        onValueChange={(v) => updateTaskConfig('deadline_reminder', 'interval_hours', parseInt(v))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="12">Every 12 hours</SelectItem>
                          <SelectItem value="24">Every 24 hours</SelectItem>
                          <SelectItem value="48">Every 48 hours</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Days Ahead</Label>
                      <Select
                        value={String(config.deadline_reminder?.days_ahead || 7)}
                        onValueChange={(v) => updateTaskConfig('deadline_reminder', 'days_ahead', parseInt(v))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="3">3 days</SelectItem>
                          <SelectItem value="7">7 days</SelectItem>
                          <SelectItem value="14">14 days</SelectItem>
                          <SelectItem value="30">30 days</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {status?.tasks?.deadline_reminder?.last_run && (
                    <p className="text-xs text-slate-500">
                      Last run: {format(parseISO(status.tasks.deadline_reminder.last_run), 'MMM dd, HH:mm')}
                    </p>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => runTask('deadline_reminder')}
                    disabled={running.deadline_reminder}
                    className="w-full"
                  >
                    {running.deadline_reminder ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                    Run Now
                  </Button>
                </CardContent>
              </Card>

              {/* Compliance Scan */}
              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-slate-900 flex items-center gap-2">
                      <Shield className="w-5 h-5 text-green-600" />
                      Compliance Scan
                    </CardTitle>
                    <Switch
                      checked={config.compliance_scan?.enabled || false}
                      onCheckedChange={(v) => updateTaskConfig('compliance_scan', 'enabled', v)}
                    />
                  </div>
                  <CardDescription>Auto-assess unscanned documents for compliance</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Scan Interval</Label>
                    <Select
                      value={String(config.compliance_scan?.interval_hours || 24)}
                      onValueChange={(v) => updateTaskConfig('compliance_scan', 'interval_hours', parseInt(v))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="6">Every 6 hours</SelectItem>
                        <SelectItem value="12">Every 12 hours</SelectItem>
                        <SelectItem value="24">Every 24 hours</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {status?.tasks?.compliance_scan?.last_run && (
                    <p className="text-xs text-slate-500">
                      Last run: {format(parseISO(status.tasks.compliance_scan.last_run), 'MMM dd, HH:mm')}
                    </p>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => runTask('compliance_scan')}
                    disabled={running.compliance_scan}
                    className="w-full"
                  >
                    {running.compliance_scan ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                    Run Now
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button onClick={saveConfig} disabled={saving} className="bg-orange-600 hover:bg-orange-700">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Configuration
              </Button>
            </div>
          </TabsContent>

          {/* Logs Tab */}
          <TabsContent value="logs" className="space-y-4">
            {logs.length === 0 ? (
              <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                <CardContent className="p-12 text-center">
                  <Clock className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">No Execution Logs</h3>
                  <p className="text-slate-600">Scheduler activity will appear here</p>
                </CardContent>
              </Card>
            ) : (
              <ScrollArea className="h-[600px]">
                <div className="space-y-2">
                  {logs.map((log, idx) => (
                    <Card key={idx} className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              log.status === 'success' ? 'bg-green-500/20' : 'bg-red-500/20'
                            }`}>
                              {log.status === 'success' ? (
                                <CheckCircle className="w-5 h-5 text-green-600" />
                              ) : (
                                <XCircle className="w-5 h-5 text-red-600" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{log.task_name.replace(/_/g, ' ')}</p>
                              <p className="text-xs text-slate-500">
                                {log.executed_at ? format(parseISO(log.executed_at), 'MMM dd, yyyy HH:mm:ss') : 'N/A'}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge className={log.status === 'success' ? 'bg-green-500' : 'bg-red-500'}>
                              {log.status}
                            </Badge>
                            {log.details && Object.keys(log.details).length > 0 && (
                              <p className="text-xs text-slate-500 mt-1">
                                {Object.entries(log.details).map(([k, v]) => `${k}: ${v}`).join(', ')}
                              </p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
